import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import toast from "react-hot-toast";
import DayViewCalendar from "./DayViewCalendar";
import {
  addNewDoc,
  getAllDocsSnap,
} from "../../../utils/firebase/Firebase.utils";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  convertTimeStamptoDate,
  dualNotification,
} from "../../../utils/EleghoUtils";
import { FaRegClock, FaRegCalendarAlt, FaMoneyBillWave } from "react-icons/fa";
import { BsCalendarCheck, BsArrowRightShort } from "react-icons/bs";
import { notifyFacilityBooking } from "../../../utils/userActivityNotifier";
import { adminEmailAddress } from "../../../utils/constants";

export default function FunctionRoomForm({ facility }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    date: new Date(),
    startTime: "09:00",
    endTime: "10:00",
  });
  const currentUser = useSelector((state) => state.userObject);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dateSelected, setDateSelected] = useState(null);
  const [existingBooking, setExistingBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeOptions, setTimeOptions] = useState([]);
  const [timeDisplayOptions, setTimeDisplayOptions] = useState([]);

  useEffect(() => {
    // Generate time options in 30-minute intervals from 7am to 11pm
    const generateTimeOptions = () => {
      const options = [];
      const displayOptions = [];

      for (let hour = 7; hour <= 23; hour++) {
        const hourString = hour.toString().padStart(2, "0");
        const timeValue = `${hourString}:00`;
        options.push(timeValue);

        // Convert to AM/PM format for display
        displayOptions.push({
          value: timeValue,
          label: formatTimeToAMPM(timeValue),
        });

        if (hour < 23) {
          // Don't add 23:30 as it would go beyond 11pm
          const timeValue30 = `${hourString}:30`;
          options.push(timeValue30);
          displayOptions.push({
            value: timeValue30,
            label: formatTimeToAMPM(timeValue30),
          });
        }
      }

      setTimeOptions(options);
      setTimeDisplayOptions(displayOptions);
    };

    generateTimeOptions();
  }, []);

  // Helper function to convert 24h time to AM/PM format
  const formatTimeToAMPM = (time24h) => {
    const [hours, minutes] = time24h.split(":");
    const hour = parseInt(hours, 10);

    const period = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

    return `${hour12}:${minutes} ${period}`;
  };

  useEffect(() => {
    const fetchBookings = async () => {
      await getAllDocsSnap("bookings", (data) => {
        const filtered = data.filter((item) => item.bookingType === facility);

        setExistingBooking(filtered);
        setLoading(false);
      });
    };
    fetchBookings();
  }, []);

  useEffect(() => {
    if (formData.startTime && formData.endTime) {
      if (isOverlapping()) {
        toast.error("Booking is overlapping with an existing booking");
      }
    }
  }, [formData]);

  // Update end time if it's earlier than or equal to the start time
  useEffect(() => {
    const startTimeValue = getTimeValue(formData.startTime);
    const endTimeValue = getTimeValue(formData.endTime);

    if (endTimeValue <= startTimeValue) {
      // Find next available time after start time
      const startIndex = timeOptions.findIndex(
        (time) => time === formData.startTime
      );
      if (startIndex !== -1 && startIndex < timeOptions.length - 1) {
        setFormData((prev) => ({
          ...prev,
          endTime: timeOptions[startIndex + 1],
        }));
      }
    }
  }, [formData.startTime, timeOptions]);

  // Helper function to convert time string to numeric value for comparison
  const getTimeValue = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 60 + minutes;
  };

  if (loading) {
    <div>Loading...</div>;
  }

  const handleChangeCalendar = (date) => {
    setDateSelected(date);
    setFormData({ ...formData, date: date });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Check if booking is overlapping to any existing booking
  const isOverlapping = () => {
    const startTime = new Date(formData.date);
    const endTime = new Date(formData.date);
    startTime.setHours(Number(formData.startTime.split(":")[0]));
    startTime.setMinutes(Number(formData.startTime.split(":")[1]));
    endTime.setHours(Number(formData.endTime.split(":")[0]));
    endTime.setMinutes(Number(formData.endTime.split(":")[1]));

    for (let i = 0; i < existingBooking?.length; i++) {
      const existingStart = new Date(
        convertTimeStamptoDate(existingBooking[i].date)
      );
      const existingEnd = new Date(
        convertTimeStamptoDate(existingBooking[i].date)
      );
      existingStart.setHours(
        Number(existingBooking[i].startTime.split(":")[0])
      );
      existingStart.setMinutes(
        Number(existingBooking[i].startTime.split(":")[1])
      );
      existingEnd.setHours(Number(existingBooking[i].endTime.split(":")[0]));
      existingEnd.setMinutes(Number(existingBooking[i].endTime.split(":")[1]));

      if (
        (startTime >= existingStart && startTime < existingEnd) ||
        (endTime > existingStart && endTime <= existingEnd) ||
        (startTime <= existingStart && endTime >= existingEnd)
      ) {
        return true;
      }
    }
    return false;
  };

  const handleBooking = async () => {
    if (isOverlapping()) {
      toast.error("Selected Date and Time is not available for booking");
      return;
    }

    setIsProcessing(true);

    const docObject = {
      ...formData,
      bookingType: facility,
      createdAt: new Date(),
      createdBy: currentUser.uid,
      createdName: currentUser.displayName,
      createdEmail: currentUser.email,
      user: currentUser.email,
      status: "pending",
      paymentStatus: "unpaid",
      identifier: currentUser.linkedIdentifiers[0],
    };

    const docRef = await addNewDoc("bookings", docObject);

    const notificationObject = {
      recipient: adminEmailAddress,
      recipientName: "Admin",
      sender: currentUser.email,
      senderName: currentUser.displayName,
      subject: `New Booking Request for ${facility}`,
      message: `A new booking request has been made by ${
        currentUser.displayName
      } for ${facility} on ${formData.date.toLocaleDateString()}.`,
      dateTime: new Date(),

      url: `/facilities?facility=${facility}&filter=All`,
      id: docRef.id,
    };

    await dualNotification(notificationObject);

    toast.success("Booking Successful");
    setIsProcessing(false);
    navigate("/facility-usage");
  };

  // Check if an end time option should be disabled
  const isEndTimeDisabled = (time) => {
    return getTimeValue(time) <= getTimeValue(formData.startTime);
  };

  return (
    <div className="max-w-3xl mx-auto pt-8 p-4">
      <div className="text-[#363062] font-semibold text-lg mb-8 flex items-center justify-center gap-2">
        <FaRegCalendarAlt className="text-[#8FCD00]" />
        <span>Book {facility}</span>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        {dateSelected ? (
          <div>
            <div className="flex items-center gap-2 mb-4 text-[#363062]">
              <BsCalendarCheck className="text-[#8FCD00]" />
              <span className="text-sm font-medium">Selected Date View</span>
            </div>
            <DayViewCalendar
              date={dateSelected}
              bookings={existingBooking}
              setDateSelected={setDateSelected}
              fac
            />
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-4 text-[#363062]">
              <FaRegCalendarAlt className="text-[#8FCD00]" />
              <span className="text-sm font-medium">Step 1: Select Date</span>
            </div>
            <div className="calendar-wrapper">
              <Calendar onChange={handleChangeCalendar} className="mx-auto" />
            </div>
          </div>
        )}
      </div>

      {dateSelected && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4 text-[#363062]">
            <FaRegClock className="text-[#8FCD00]" />
            <span className="text-sm font-medium">Step 2: Select Time</span>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-medium text-[#363062]">
                Start Time
              </label>
              <select
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#8FCD00] text-sm"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
              >
                {timeDisplayOptions.map((option) => (
                  <option key={`start-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-[#363062]">
                End Time
              </label>
              <select
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#8FCD00] text-sm"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
              >
                {timeDisplayOptions.map((option) => (
                  <option
                    key={`end-${option.value}`}
                    value={option.value}
                    disabled={isEndTimeDisabled(option.value)}
                    className={
                      isEndTimeDisabled(option.value) ? "text-gray-400" : ""
                    }
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-4 text-[#363062]">
          <FaMoneyBillWave className="text-[#8FCD00]" />
          <span className="text-sm font-medium">Booking Summary</span>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Facility Fee</span>
            <span className="font-medium text-[#363062]">₱xx</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Security Deposit</span>
            <span className="font-medium text-[#363062]">₱xx</span>
          </div>
          <div className="h-px bg-gray-200 my-2"></div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-[#363062]">
              Total Amount
            </span>
            <span className="font-semibold text-[#363062]">₱xx</span>
          </div>
        </div>
      </div>

      <div>
        <input
          type="checkbox"
          name="rules"
          value={formData.rules}
          onChange={(e) =>
            setFormData({ ...formData, rules: e.target.checked })
          }
        />
        <span className="text-sm text-gray-600 ml-2 ">
          I agree to follow the rules and regulations of the facility. I will be
          responsible for any damages or issues that arise during my booking.
        </span>
      </div>

      <button
        className="mt-4 w-full py-3 px-6 rounded-xl bg-[#8FCD00] hover:bg-[#7ab500] transition-colors text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={handleBooking}
        disabled={isProcessing || dateSelected === null || !formData.rules}
      >
        {isProcessing ? (
          "Processing..."
        ) : (
          <>
            <span className="">Proceed to Book</span>
            <BsArrowRightShort className="text-xl" />
          </>
        )}
      </button>
    </div>
  );
}
